import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

import SummaryTrial from "./SummaryTrial.jsx";
import NavBar from "../../components/navBar/navBar.js";
import AddNewTrial from "../../components/PopUp/AddNewTrial.js";
import Loader from "../../components/Loader.jsx";
import { LoaderContext } from "../../context/LoaderContext.jsx";
import useUser from "../../hooks/user.jsx";
import logUserAction from "../../apis/userLogger.js";
import { __API_URL__ } from "../../SERVER_URL.jsx";
import useAddTrialMutation from "../../hooks/useAddTrialMutation.js";
import useDuplicateTrialMutation from "../../hooks/useDuplicateTrialMutation.js";
import useGetTrialsList from "../../hooks/useGetTrialsList.js";
import { INV_RELATED_ROLES, CRO_RELATED_ROLES } from "../../utils/constants.js";

const COLUMN_VISIBILITY_STORAGE_KEY = "trials_list_column_visibility";

const HEADER_AMBER = "#B45309";
const HEADER_NEUTRAL_BG = "#F3F4F6";
const STATUS_COLORS = {
  Active: "#3B6D11",
  Closed: "#6B7280",
  Draft: "#854F0B",
};
const GROUP_LABELS = {
  enrollment: "Enrollment",
  study_start: "Study Start",
  completion: "Completion",
};
const GROUP_STYLES = {
  enrollment: { bg: "rgba(97,196,89,0.1)", color: "#3B6D11" },
  study_start: { bg: "rgba(55,138,221,0.1)", color: "#185FA5" },
  completion: { bg: "rgba(239,159,39,0.12)", color: "#854F0B" },
};
const GROUP_ROW_STYLES = {
  enrollment: { borderLeft: "2px solid rgba(59,109,17,0.35)", background: "rgba(97,196,89,0.035)" },
  study_start: {
    borderLeft: "2px solid rgba(24,95,165,0.35)",
    background: "rgba(55,138,221,0.035)",
  },
  completion: {
    borderLeft: "2px solid rgba(133,79,11,0.35)",
    background: "rgba(239,159,39,0.045)",
  },
};
const GROUP_HEADER_LEAF_BG = {
  enrollment: "#F3F8F1",
  study_start: "#EFF4FB",
  completion: "#FBF3EA",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const DateCell = ({ actual, expected }) => {
  const a = fmtDate(actual);
  const e = fmtDate(expected);
  if (a) {
    return (
      <Box>
        <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#3B6D11" }}>
          Actual
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{a}</Typography>
        {e && (
          <>
            <Typography sx={{ fontSize: "0.65rem", color: "text.disabled", mt: 0.25 }}>
              Expected
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{e}</Typography>
          </>
        )}
      </Box>
    );
  }
  if (e) {
    return (
      <Box>
        <Typography sx={{ fontSize: "0.65rem", color: "text.disabled" }}>Expected</Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{e}</Typography>
      </Box>
    );
  }
  return <Typography sx={{ color: "text.disabled" }}>—</Typography>;
};

const EnvBadge = ({ env }) => {
  const isLive = env === "Live";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: 10,
        fontSize: "0.7rem",
        fontWeight: 600,
        background: isLive ? "#EAF3DE" : "#FAEEDA",
        color: isLive ? "#3B6D11" : "#854F0B",
      }}
    >
      {env || "—"}
    </Box>
  );
};

function TrialsPagination({ page, pageSize, rowCount, onPageChange, onPageSizeChange }) {
  const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        px: 2,
        py: 1.5,
      }}
    >
      <Select
        size="small"
        variant="standard"
        disableUnderline
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        sx={{ fontSize: "0.85rem" }}
      >
        {[10, 25, 50, 100].map((n) => (
          <MenuItem key={n} value={n}>
            Show {n}
          </MenuItem>
        ))}
      </Select>
      <Pagination
        page={page + 1}
        count={pageCount}
        onChange={(_e, value) => onPageChange(value - 1)}
        shape="rounded"
        color="primary"
        size="small"
      />
      <Typography variant="body2" color="text.secondary">
        {rowCount} trials
      </Typography>
    </Box>
  );
}

const buildDateColumn = (key, label, actualField, expectedField, groupId) => ({
  key,
  label,
  groupId,
  sortable: true,
  getValue: (row) => {
    const v = row[actualField] || row[expectedField];
    return v ? new Date(v).getTime() : null;
  },
  exportValue: (row) => fmtDate(row[actualField] || row[expectedField]) || "—",
  renderCell: (row) => <DateCell actual={row[actualField]} expected={row[expectedField]} />,
});

const TrialsList = () => {
  const user = useUser();
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const isInvRole = INV_RELATED_ROLES.includes(role);
  const canManageTrials = CRO_RELATED_ROLES.includes(role);
  const loaderContext = useContext(LoaderContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [envFilter, setEnvFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItemCount, setPageItemCount] = useState(10);
  const navigate = useNavigate();

  const [sortState, setSortState] = useState({ field: null, direction: "asc" });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [columnSearch, setColumnSearch] = useState("");

  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
    } catch {
      // localStorage unavailable — column visibility just won't persist across sessions
    }
  }, [columnVisibility]);

  const { data: trialsData, isLoading: trialsLoading } = useGetTrialsList({
    page: currentPage,
    pageSize: pageItemCount,
    search: searchTerm,
    environment: envFilter,
  });

  const trials = useMemo(() => trialsData?.results || [], [trialsData]);
  const totalCount = trialsData?.count || 0;

  const addTrialMutation = useAddTrialMutation();
  const duplicateTrialMutation = useDuplicateTrialMutation();

  const defaultTrialInfo = {
    trial_custom_id: "",
    name: "",
    status: "",
    investigators: [],
    trial_modules: [],
    trial_structure: "visit_based",
    trial_type: "",
    environment: "UAT",
    expected_first_enrollment: null,
    expected_last_enrollment: null,
    expected_study_start_date: null,
    expected_study_completion_date: null,
  };

  const [isEdit, setIsEdit] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateSourceId, setDuplicateSourceId] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [trialInfo, setTrialInfo] = useState(defaultTrialInfo);
  const [summaryModalShow, setSummaryModalShow] = useState(false);

  const handleRowClick = useCallback(
    (trial) => {
      navigate("/clinic_trials/" + trial.id + "/summary");
      logUserAction("Click Trial From Trial list ", "-", `${trial.name}`);
    },
    [navigate]
  );

  const handleModalHide = () => {
    setModalShow(false);
    setIsEdit(false);
    setIsDuplicate(false);
    setDuplicateSourceId(null);
    setTrialInfo(defaultTrialInfo);
  };

  const handleDuplicateClick = useCallback((e, trial) => {
    e.stopPropagation();
    setIsDuplicate(true);
    setIsEdit(false);
    setDuplicateSourceId(trial.id);
    setTrialInfo({
      ...defaultTrialInfo,
      trial_custom_id: "",
      name: `Copy of ${trial.name}`,
      status: "Active",
      trial_structure: trial.trial_structure || "visit_based",
      trial_type: trial.trial_type || "",
      environment: trial.environment || "UAT",
      expected_first_enrollment: trial.expected_first_enrollment || null,
      expected_last_enrollment: trial.expected_last_enrollment || null,
      expected_study_start_date: trial.expected_study_start_date || null,
      expected_study_completion_date: trial.expected_study_completion_date || null,
    });
    setModalShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const AllInvestigatorsData = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/investigatorsList/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        let customData = [];
        res.data.forEach((obj) => {
          customData.push({
            value: obj.email,
            label: obj.email,
          });
        });
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  useEffect(() => {
    AllInvestigatorsData();
  }, []);

  const handleAddTrial = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    if (isDuplicate) {
      duplicateTrialMutation.mutate(
        { sourceTrialId: duplicateSourceId, trialData: trialInfo },
        {
          onSuccess: () => {
            toast.success("Trial duplicated successfully");
            handleModalHide();
          },
          onError: (err) => {
            handleModalHide();
            toast.error(err?.response?.data?.message || "Failed to duplicate trial");
          },
          onSettled: () => {
            loaderContext.setLoaderCount((prev) => prev - 1);
          },
        }
      );
      return;
    }

    if (isEdit) {
      axios
        .patch(`${__API_URL__}/api/photos/edit_trial/${trialInfo.id}/`, trialInfo, {
          headers: {
            Authorization: `Token ${token}`,
          },
        })
        .then(() => {
          toast.success("Edited Successfully");
          setModalShow(false);
          setTrialInfo(defaultTrialInfo);
          window.location.reload();
        })
        .catch((err) => {
          setModalShow(false);
          setTrialInfo(defaultTrialInfo);
          if (err === "Error: Request failed with status code 401") {
            user.actions.logout();
          }
          toast.error(err.response.data.message);
        })
        .finally(() => {
          loaderContext.setLoaderCount((prev) => prev - 1);
        });
    } else {
      addTrialMutation.mutate(trialInfo, {
        onSuccess: () => {
          toast.success("Added Successfully");
          setModalShow(false);
          setTrialInfo(defaultTrialInfo);
        },
        onError: (err) => {
          setModalShow(false);
          setTrialInfo(defaultTrialInfo);
          toast.error(err?.response?.data?.message || "Failed to add trial");
        },
        onSettled: () => {
          loaderContext.setLoaderCount((prev) => prev - 1);
        },
      });
    }
  };

  const columns = useMemo(() => {
    const base = [
      {
        key: "trial_custom_id",
        label: "Trial ID",
        groupId: null,
        sortable: true,
        getValue: (row) => row.trial_custom_id || "",
        renderCell: (row) => row.trial_custom_id,
      },
      {
        key: "name",
        label: "Trial Name",
        groupId: null,
        sortable: true,
        getValue: (row) => row.name || "",
        renderCell: (row) => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            {row.name}
            {canManageTrials && (
              <Tooltip title="Duplicate Trial">
                <IconButton
                  className="duplicate-trial-btn"
                  data-testid="duplicate-trial-btn"
                  size="small"
                  onClick={(e) => handleDuplicateClick(e, row)}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s",
                    ".trials-row:hover &": { opacity: 1 },
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        key: "status",
        label: "Status",
        groupId: null,
        sortable: true,
        getValue: (row) => row.status || "",
        renderCell: (row) => (
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 600,
              color: STATUS_COLORS[row.status] || "text.secondary",
            }}
          >
            {row.status}
          </Typography>
        ),
      },
      {
        key: "environment",
        label: "Env",
        groupId: null,
        sortable: true,
        getValue: (row) => row.environment || "",
        renderCell: (row) => <EnvBadge env={row.environment} />,
      },
    ];

    if (isInvRole) {
      return base;
    }

    return [
      ...base,
      {
        key: "disease_type",
        label: "Disease Type",
        groupId: null,
        sortable: true,
        getValue: (row) => row.disease_type || "",
        renderCell: (row) => row.disease_type ?? "—",
      },
      {
        key: "site_count",
        label: "Sites",
        groupId: null,
        width: 55,
        sortable: true,
        getValue: (row) => row.site_count ?? 0,
        renderCell: (row) => row.site_count,
      },
      {
        key: "subject_count",
        label: "Subjects",
        groupId: null,
        width: 65,
        sortable: true,
        getValue: (row) => row.subject_count ?? 0,
        renderCell: (row) => row.subject_count,
      },
      {
        key: "target_enrolled",
        label: "Target Enrolled",
        groupId: "enrollment",
        width: 75,
        headerWrap: true,
        sortable: true,
        getValue: (row) => row.target_enrolled ?? 0,
        renderCell: (row) => row.target_enrolled,
      },
      {
        key: "total_enrolled",
        label: "Total Enrolled",
        groupId: "enrollment",
        width: 75,
        headerWrap: true,
        sortable: true,
        getValue: (row) => row.total_enrolled ?? 0,
        renderCell: (row) => row.total_enrolled,
      },
      {
        key: "eligible_subject_count",
        label: "Eligible Subjects",
        groupId: "enrollment",
        width: 85,
        headerWrap: true,
        sortable: true,
        getValue: (row) => row.eligible_subject_count ?? 0,
        renderCell: (row) => row.eligible_subject_count,
      },
      buildDateColumn(
        "actual_first_enrollment",
        "1st Enrollment",
        "actual_first_enrollment",
        "expected_first_enrollment",
        "enrollment"
      ),
      buildDateColumn(
        "actual_last_enrollment",
        "Last Enrollment",
        "actual_last_enrollment",
        "expected_last_enrollment",
        "enrollment"
      ),
      buildDateColumn(
        "actual_study_start_date",
        "Start Date",
        "actual_study_start_date",
        "expected_study_start_date",
        "study_start"
      ),
      {
        key: "created_at",
        label: "Created",
        groupId: "study_start",
        sortable: true,
        getValue: (row) => (row.created_at ? new Date(row.created_at).getTime() : null),
        exportValue: (row) => fmtDate(row.created_at) || "—",
        renderCell: (row) => fmtDate(row.created_at) || "—",
      },
      buildDateColumn(
        "actual_study_completion_date",
        "Completion",
        "actual_study_completion_date",
        "expected_study_completion_date",
        "completion"
      ),
      {
        key: "cro_admin",
        label: "CRO Admin",
        groupId: null,
        sortable: true,
        getValue: (row) => row.cro_admin || "",
        renderCell: (row) => row.cro_admin ?? "—",
      },
    ];
  }, [isInvRole, canManageTrials, handleDuplicateClick]);

  const columnsByKey = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns]);

  const isColumnVisible = useCallback((key) => columnVisibility[key] !== false, [columnVisibility]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => isColumnVisible(c.key)),
    [columns, isColumnVisible]
  );

  const headerRuns = useMemo(() => {
    const runs = [];
    visibleColumns.forEach((col) => {
      const last = runs[runs.length - 1];
      if (col.groupId && last && last.groupId === col.groupId) {
        last.columns.push(col);
      } else {
        runs.push({ groupId: col.groupId, columns: [col] });
      }
    });
    return runs;
  }, [visibleColumns]);

  const hasGroups = headerRuns.some((run) => run.groupId);

  const groupRunStartKeys = useMemo(
    () => new Set(headerRuns.filter((run) => run.groupId).map((run) => run.columns[0].key)),
    [headerRuns]
  );

  const groupCellSx = (col, { header = false } = {}) => {
    if (!col.groupId) return {};
    const rowStyle = GROUP_ROW_STYLES[col.groupId];
    return {
      background: header ? GROUP_HEADER_LEAF_BG[col.groupId] : rowStyle?.background,
      borderLeft: groupRunStartKeys.has(col.key) ? rowStyle?.borderLeft : undefined,
    };
  };

  const sortedTrials = useMemo(() => {
    if (!sortState.field) return trials;
    const col = columnsByKey[sortState.field];
    if (!col) return trials;
    const dir = sortState.direction === "desc" ? -1 : 1;
    return [...trials].sort((a, b) => {
      const av = col.getValue(a);
      const bv = col.getValue(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [trials, sortState, columnsByKey]);

  const handleSort = (key) => {
    setSortState((prev) => {
      if (prev.field !== key) return { field: key, direction: "asc" };
      return { field: key, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
  };

  const pageIds = useMemo(() => trials.map((t) => t.id), [trials]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const allColumnsVisible = columns.every((c) => isColumnVisible(c.key));
  const someColumnsVisible = columns.some((c) => isColumnVisible(c.key));

  const toggleAllColumns = () => {
    const next = {};
    columns.forEach((c) => {
      next[c.key] = !allColumnsVisible;
    });
    setColumnVisibility(next);
  };

  const resetColumns = () => setColumnVisibility({});

  const filteredColumnsForMenu = useMemo(
    () => columns.filter((c) => c.label.toLowerCase().includes(columnSearch.toLowerCase())),
    [columns, columnSearch]
  );

  const handleExportClick = () => {
    const data = sortedTrials.map((row) => {
      const obj = {};
      visibleColumns.forEach((c) => {
        obj[c.label] = c.exportValue ? c.exportValue(row) : c.getValue(row);
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = { Sheets: { Trials: worksheet }, SheetNames: ["Trials"] };
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, "trials_export.xlsx");
    toast.success("Data exported successfully");
    logUserAction("Click on Export to Excel Button (Trials)");
  };

  const renderHeaderLabel = (col) => {
    const labelSx = {
      fontWeight: 700,
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: 0.3,
      ...(col.headerWrap && {
        whiteSpace: "normal",
        lineHeight: 1.15,
        textAlign: "center",
      }),
    };
    if (!col.sortable) {
      return <Typography sx={{ ...labelSx, color: HEADER_AMBER }}>{col.label}</Typography>;
    }
    return (
      <TableSortLabel
        active={sortState.field === col.key}
        direction={sortState.field === col.key ? sortState.direction : "asc"}
        onClick={() => handleSort(col.key)}
        sx={{
          ...labelSx,
          color: `${HEADER_AMBER} !important`,
          "& .MuiTableSortLabel-icon": { color: `${HEADER_AMBER} !important` },
        }}
      >
        {col.label}
      </TableSortLabel>
    );
  };

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="trials" />

      {loaderContext.loaderCount > 0 || trialsLoading ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              pt: 2,
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search by Trial Name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{ maxWidth: 300, "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  },
                  htmlInput: { "data-testid": "trial-search-input" },
                }}
              />
              <Box sx={{ display: "flex", gap: 0.75 }}>
                {[
                  { value: "all", label: "All" },
                  { value: "Live", label: "Live" },
                  { value: "UAT", label: "UAT" },
                ].map(({ value, label }) => (
                  <Button
                    key={value}
                    size="small"
                    variant={envFilter === value ? "contained" : "outlined"}
                    onClick={() => {
                      setEnvFilter(value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      fontSize: "0.75rem",
                      borderRadius: 10,
                      textTransform: "none",
                      ...(envFilter === value &&
                        value === "Live" && {
                          background: "#EAF3DE",
                          color: "#3B6D11",
                          borderColor: "#97C459",
                          "&:hover": { background: "#d6ebc4" },
                        }),
                      ...(envFilter === value &&
                        value === "UAT" && {
                          background: "#FAEEDA",
                          color: "#854F0B",
                          borderColor: "#EF9F27",
                          "&:hover": { background: "#f5ddb8" },
                        }),
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
              <Button
                size="small"
                variant="text"
                startIcon={<ViewColumnIcon fontSize="small" />}
                onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
                sx={{ textTransform: "none" }}
              >
                Columns
              </Button>
              <Popover
                open={Boolean(columnsMenuAnchor)}
                anchorEl={columnsMenuAnchor}
                onClose={() => {
                  setColumnsMenuAnchor(null);
                  setColumnSearch("");
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box sx={{ width: 260, p: 1.5 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search"
                    value={columnSearch}
                    onChange={(e) => setColumnSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
                    {filteredColumnsForMenu.map((col) => (
                      <Box
                        key={col.key}
                        sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                        onClick={() => toggleColumn(col.key)}
                      >
                        <Checkbox size="small" checked={isColumnVisible(col.key)} />
                        <Typography sx={{ fontSize: "0.85rem" }}>{col.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                      onClick={toggleAllColumns}
                    >
                      <Checkbox
                        size="small"
                        checked={allColumnsVisible}
                        indeterminate={!allColumnsVisible && someColumnsVisible}
                      />
                      <Typography sx={{ fontSize: "0.85rem" }}>Show/Hide All</Typography>
                    </Box>
                    <Button size="small" onClick={resetColumns} sx={{ textTransform: "none" }}>
                      Reset
                    </Button>
                  </Box>
                </Box>
              </Popover>
              <Button
                size="small"
                variant="contained"
                onClick={handleExportClick}
                sx={{ textTransform: "none" }}
              >
                Export to Excel
              </Button>
            </Box>
            {canManageTrials && (
              <Button
                variant="contained"
                size="small"
                data-testid="add-trial-btn"
                onClick={() => {
                  setModalShow(true);
                  setIsEdit(false);
                  logUserAction("Click on Add Trial Button");
                }}
              >
                Add Trial
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: isInvRole ? undefined : 1200,
                "& .MuiTableCell-root": { px: 1.25 },
              }}
            >
              <TableHead>
                {hasGroups ? (
                  <>
                    <TableRow>
                      <TableCell
                        padding="checkbox"
                        rowSpan={2}
                        sx={{ backgroundColor: HEADER_NEUTRAL_BG }}
                      >
                        <Checkbox
                          size="small"
                          checked={allPageSelected}
                          indeterminate={!allPageSelected && somePageSelected}
                          onChange={toggleSelectAll}
                          inputProps={{ "aria-label": "Select all rows on page" }}
                        />
                      </TableCell>
                      {headerRuns.map((run, idx) =>
                        run.groupId ? (
                          <TableCell
                            key={`${run.groupId}-${idx}`}
                            colSpan={run.columns.length}
                            align="center"
                            sx={{
                              backgroundColor: GROUP_STYLES[run.groupId]?.bg,
                              color: GROUP_STYLES[run.groupId]?.color,
                              borderLeft: `2px solid ${GROUP_STYLES[run.groupId]?.color}`,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              letterSpacing: 0.3,
                              py: 0.5,
                            }}
                          >
                            {GROUP_LABELS[run.groupId]}
                          </TableCell>
                        ) : (
                          run.columns.map((col) => (
                            <TableCell
                              key={col.key}
                              rowSpan={2}
                              align="center"
                              sx={{
                                whiteSpace: "nowrap",
                                verticalAlign: "bottom",
                                width: col.width,
                                backgroundColor: HEADER_NEUTRAL_BG,
                              }}
                            >
                              {renderHeaderLabel(col)}
                            </TableCell>
                          ))
                        )
                      )}
                    </TableRow>
                    <TableRow>
                      {headerRuns.flatMap((run) =>
                        run.groupId
                          ? run.columns.map((col) => (
                              <TableCell
                                key={col.key}
                                align="center"
                                sx={{
                                  whiteSpace: col.headerWrap ? "normal" : "nowrap",
                                  width: col.width,
                                  ...groupCellSx(col, { header: true }),
                                }}
                              >
                                {renderHeaderLabel(col)}
                              </TableCell>
                            ))
                          : []
                      )}
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ backgroundColor: HEADER_NEUTRAL_BG }}>
                      <Checkbox
                        size="small"
                        checked={allPageSelected}
                        indeterminate={!allPageSelected && somePageSelected}
                        onChange={toggleSelectAll}
                        inputProps={{ "aria-label": "Select all rows on page" }}
                      />
                    </TableCell>
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        align="center"
                        sx={{
                          whiteSpace: "nowrap",
                          width: col.width,
                          backgroundColor: HEADER_NEUTRAL_BG,
                        }}
                      >
                        {renderHeaderLabel(col)}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {sortedTrials.length > 0 ? (
                  sortedTrials.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      className="trials-row"
                      onClick={() => handleRowClick(row)}
                      sx={{ cursor: "pointer" }}
                      data-testid="trial-row"
                      data-trial-name={row.name}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleSelectRow(row.id)}
                          inputProps={{ "aria-label": `Select ${row.name}` }}
                        />
                      </TableCell>
                      {visibleColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          align="center"
                          sx={{
                            fontSize: "0.8rem",
                            whiteSpace: "nowrap",
                            width: col.width,
                            ...groupCellSx(col),
                          }}
                        >
                          {col.renderCell(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No Matching Trial Name Found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TrialsPagination
            page={currentPage - 1}
            pageSize={pageItemCount}
            rowCount={totalCount}
            onPageChange={(newPage) => setCurrentPage(newPage + 1)}
            onPageSizeChange={(newSize) => {
              setPageItemCount(newSize);
              setCurrentPage(1);
            }}
          />
        </>
      )}

      {summaryModalShow && <SummaryTrial />}
      {modalShow && (
        <AddNewTrial
          isEdit={isEdit}
          isDuplicate={isDuplicate}
          trialInfo={trialInfo}
          setTrialInfo={setTrialInfo}
          handleModalHide={handleModalHide}
          handleAddTrial={handleAddTrial}
        />
      )}
    </Box>
  );
};

export default TrialsList;
